import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Input,
  FormGroup,
  Label,
  Spinner,
  Table,
} from "reactstrap";
import { formatDeliveryChargesSummaryDisplay } from "../components/OrderSummaryPriceMetaRows";
import StoreMultiSelectFilter from "../components/StoreMultiSelectFilter";
import {
  canAddPaymentsInSalesModal,
  ensureArray,
  fmtHoverDate,
  fmtHoverDateTime,
  fmtOrderRupee,
  fmtRelativeTime,
  formatBuyerFirstUserLine,
  formatStoreOrderLabel,
  formatStoreProjectStatusWord,
  ORDER_ACCEPTED_DATE_STATUS_FILTERS,
  ORDER_MONTH_FILTER_OPTIONS,
  ORDER_SAMPLE_FILTER_OPTIONS,
  ORDER_STATUS_OPTIONS,
  ordersValueTotal,
  downloadInvoiceDetailsCsv,
} from "./storeSalesDashboardHelpers";
import { SAMPLE_ORDER_REJECTED_MESSAGE } from "./storeProjectDetail/projectActionDock";

const isHttpUrl = (value) => {
  if (typeof value !== "string") return false;
  return /^https?:\/\//i.test(value.trim());
};

function formatDeliveryInfoValue(raw) {
  const text = String(raw ?? "").trim();
  return text || "—";
}

function OrderDeliveryInfoCell({ row }) {
  const address = formatDeliveryInfoValue(row?.delivery_address);
  const pincode = formatDeliveryInfoValue(row?.delivery_pincode);
  const contact = formatDeliveryInfoValue(row?.delivery_contact);
  const locationRaw = String(row?.delivery_location ?? "").trim();
  const locationDisplay = locationRaw || "—";

  return (
    <div className="small d-flex flex-column gap-1">
      <div>
        <span className="text-muted">Address: </span>
        {address}
      </div>
      <div>
        <span className="text-muted">Pincode: </span>
        {pincode}
      </div>
      <div>
        <span className="text-muted">Contact: </span>
        {contact}
      </div>
      <div>
        <span className="text-muted">Location: </span>
        {isHttpUrl(locationRaw) ? (
          <a
            href={locationRaw}
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-none"
          >
            Open map
          </a>
        ) : (
          locationDisplay
        )}
      </div>
    </div>
  );
}

function orderMetaTotalFromMeta(meta) {
  if (!meta || typeof meta !== "object") return null;
  const n = Number(meta.total);
  return Number.isFinite(n) ? n : null;
}

const toNumberOrNull = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const ORDER_STATUS_FILTER_OPTIONS = ORDER_STATUS_OPTIONS.filter(
  (option) => option.value !== "dealer_accepted"
);

const ORDER_MONTH_MULTI_FILTER_OPTIONS = ORDER_MONTH_FILTER_OPTIONS.filter(
  (option) => option.value
);

const formatBreakdownCurrency = (value) => {
  const n = toNumberOrNull(value);
  if (n == null) return "—";

  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

function OrderPriceMetaSummaryRow({
  label,
  value,
  bold = false,
  className = "",
}) {
  return (
    <div className={`d-flex justify-content-between py-1 ${className}`.trim()}>
      <span className={bold ? "fw-semibold" : "text-muted"}>{label}</span>
      <span className={bold ? "fw-bold" : undefined}>{value}</span>
    </div>
  );
}

function OrderPriceMetaBreakdown({ meta }) {
  const rows = useMemo(() => {
    if (!meta || typeof meta !== "object") return null;

    const discounts = Array.isArray(meta?.discounts) ? meta.discounts : [];
    const baseDiscount = discounts.find((d) => d?.type === "base");
    const onboardingDiscount = discounts.find((d) => d?.type === "onboarding");

    const baseValue = toNumberOrNull(meta?.base_value);
    const creditNotes = toNumberOrNull(meta?.used_credit_notes) ?? 0;
    const taxableValue = toNumberOrNull(meta?.total_after_credit_notes);
    const gst = toNumberOrNull(meta?.gst);
    const totalAfterGst = toNumberOrNull(meta?.total_after_gst);
    const deliveryCharge = toNumberOrNull(meta?.delivery_charge) ?? 0;
    const deliveryGst = toNumberOrNull(meta?.delivery_gst) ?? 0;
    const deliveryTotal = deliveryCharge + deliveryGst;
    const payable = toNumberOrNull(meta?.total);

    return {
      baseValue,
      baseDiscount,
      onboardingDiscount,
      creditNotes,
      taxableValue,
      gst,
      totalAfterGst,
      deliveryTotal,
      deliveryCharge,
      deliveryGst,
      payable,
    };
  }, [meta]);

  if (!rows) {
    return <p className="text-muted mb-0 small">No price summary available.</p>;
  }

  const {
    baseValue,
    baseDiscount,
    onboardingDiscount,
    creditNotes,
    taxableValue,
    gst,
    totalAfterGst,
    deliveryTotal,
    deliveryCharge,
    deliveryGst,
    payable,
  } = rows;

  return (
    <div className="small">
      <OrderPriceMetaSummaryRow
        label="Item Total"
        value={formatBreakdownCurrency(baseValue)}
      />
      {baseDiscount ? (
        <>
          <OrderPriceMetaSummaryRow
            label="Base discount"
            value={`-${formatBreakdownCurrency(
              baseDiscount?.discount_value
            )} (${baseDiscount?.percentage}% of ${formatBreakdownCurrency(
              baseDiscount?.value
            )})`}
          />
          <OrderPriceMetaSummaryRow
            label="Sub-total"
            value={formatBreakdownCurrency(baseDiscount?.effective_value)}
          />
        </>
      ) : null}
      {onboardingDiscount ? (
        <>
          <OrderPriceMetaSummaryRow
            label="Onboarding discount"
            value={`-${formatBreakdownCurrency(
              onboardingDiscount?.discount_value
            )} (${onboardingDiscount?.percentage}% of ${formatBreakdownCurrency(
              onboardingDiscount?.value
            )})`}
          />
          <OrderPriceMetaSummaryRow
            label="Sub-total"
            value={formatBreakdownCurrency(onboardingDiscount?.effective_value)}
          />
        </>
      ) : null}
      <OrderPriceMetaSummaryRow
        label="Credit Notes"
        value={`-${formatBreakdownCurrency(creditNotes)} (excl. GST)`}
      />
      <OrderPriceMetaSummaryRow
        label="Taxable Value"
        value={formatBreakdownCurrency(taxableValue)}
      />
      <OrderPriceMetaSummaryRow
        label="GST"
        value={formatBreakdownCurrency(gst)}
      />
      <OrderPriceMetaSummaryRow
        label="Total (incl. GST)"
        value={formatBreakdownCurrency(totalAfterGst)}
        bold
        className="border-top mt-1 pt-2"
      />
      <OrderPriceMetaSummaryRow
        label="Delivery Charges"
        value={formatDeliveryChargesSummaryDisplay(
          deliveryCharge,
          deliveryGst,
          deliveryTotal,
          formatBreakdownCurrency
        )}
      />
      <OrderPriceMetaSummaryRow
        label="Payable"
        value={formatBreakdownCurrency(payable)}
        bold
        className="border-top mt-1 pt-2"
      />
    </div>
  );
}

function OrderPriceMetaBreakdownModal({ isOpen, toggle, title, meta }) {
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody>
        <OrderPriceMetaBreakdown meta={meta} />
      </ModalBody>
    </Modal>
  );
}

function formatMetaTotalCell(meta) {
  const total = orderMetaTotalFromMeta(meta);
  if (total == null) return "—";
  return fmtOrderRupee(total);
}

function PriceMetaTotalCell({ meta, label, onOpen }) {
  const hasMeta = meta != null && typeof meta === "object";
  const display = formatMetaTotalCell(meta);

  if (!hasMeta) {
    return <span className="text-muted">{display}</span>;
  }

  return (
    <button
      type="button"
      className="btn btn-link p-0 text-decoration-none fw-semibold text-body"
      onClick={() => onOpen(meta, label)}
    >
      {display}
    </button>
  );
}

const StoreSalesDashboardOrdersSection = ({
  ordersLoading,
  ordersError,
  ordersStatusFilter,
  setOrdersStatusFilter,
  ordersSampleFilter,
  setOrdersSampleFilter,
  ordersMonthFilter,
  setOrdersMonthFilter,
  sortedOrders,
  handleOrdersSort,
  renderOrdersSortIcon,
  openBuyersTabWithBuyerHighlight,
  openDispatchShipmentModal,
  openShipmentInvoiceModal,
  openDeliverShipmentModal,
  openPaymentsModal,
  updateOrderStatus,
}) => {
  const [priceMetaModal, setPriceMetaModal] = useState({
    isOpen: false,
    meta: null,
    title: "",
  });
  const [orderStatusConfirmModal, setOrderStatusConfirmModal] = useState({
    isOpen: false,
    action: null,
    projectId: null,
    orderLabel: "",
    rejectionRemarks: "",
    loading: false,
  });

  const closeOrderStatusConfirmModal = () => {
    setOrderStatusConfirmModal({
      isOpen: false,
      action: null,
      projectId: null,
      orderLabel: "",
      rejectionRemarks: "",
      loading: false,
    });
  };

  const openOrderStatusConfirmModal = (projectId, action, orderLabel) => {
    setOrderStatusConfirmModal({
      isOpen: true,
      action,
      projectId,
      orderLabel: String(orderLabel || "").trim() || `Order #${projectId}`,
      rejectionRemarks: "",
      loading: false,
    });
  };

  const handleConfirmOrderStatusChange = async () => {
    const { projectId, action, rejectionRemarks } = orderStatusConfirmModal;
    if (projectId == null || !action) return;

    const trimmedRemarks = String(rejectionRemarks || "").trim();
    if (action === "reject" && !trimmedRemarks) return;

    setOrderStatusConfirmModal((prev) => ({ ...prev, loading: true }));
    const nextStatus = action === "accept" ? "accepted" : "rejected";
    const ok = await updateOrderStatus(
      projectId,
      nextStatus,
      action === "reject" ? trimmedRemarks : null
    );
    if (ok) {
      closeOrderStatusConfirmModal();
    } else {
      setOrderStatusConfirmModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const openPriceMetaModal = (meta, breakdownLabel) => {
    setPriceMetaModal({
      isOpen: true,
      meta,
      title: breakdownLabel,
    });
  };

  const closePriceMetaModal = () => {
    setPriceMetaModal({ isOpen: false, meta: null, title: "" });
  };

  const ordersValueSum = useMemo(
    () => ordersValueTotal(sortedOrders),
    [sortedOrders]
  );

  const statusFilters = ensureArray(ordersStatusFilter);
  const showRejectedRemarksColumn = statusFilters.includes("rejected");
  const showAcceptedDateColumn = statusFilters.some((status) =>
    ORDER_ACCEPTED_DATE_STATUS_FILTERS.includes(status)
  );
  const showRejectedDateColumn = statusFilters.includes("rejected");
  const showInvoiceDetailsAction = showAcceptedDateColumn;
  const ordersTableColSpan =
    8 +
    (showAcceptedDateColumn ? 1 : 0) +
    (showRejectedDateColumn ? 1 : 0) +
    (showRejectedRemarksColumn ? 1 : 0);

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <StoreMultiSelectFilter
            label="Status"
            options={ORDER_STATUS_FILTER_OPTIONS}
            value={ordersStatusFilter}
            onChange={setOrdersStatusFilter}
            minWidth="140px"
          />
          <StoreMultiSelectFilter
            label="Month"
            options={ORDER_MONTH_MULTI_FILTER_OPTIONS}
            value={ordersMonthFilter}
            onChange={setOrdersMonthFilter}
            minWidth="140px"
          />
          <div
            className="btn-group btn-group-sm"
            role="group"
            aria-label="Sample order filter"
          >
            {ORDER_SAMPLE_FILTER_OPTIONS.map((option) => (
              <button
                key={option.value || "all"}
                type="button"
                className={`btn ${
                  ordersSampleFilter === option.value
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setOrdersSampleFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {ordersLoading ? (
        <div className="py-4 text-center text-muted">
          <Spinner className="me-2" />
          Loading…
        </div>
      ) : ordersError ? (
        <Alert color="danger" className="mb-0" fade={false}>
          {ordersError}
        </Alert>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="small text-muted">
              {sortedOrders.length} {sortedOrders.length === 1 ? "row" : "rows"}
            </div>
            {showInvoiceDetailsAction ? (
              <Button
                color="light"
                size="sm"
                className="border text-dark"
                disabled={sortedOrders.length === 0}
                onClick={() => downloadInvoiceDetailsCsv(sortedOrders)}
              >
                Invoice Details
              </Button>
            ) : null}
          </div>
          <Table bordered hover responsive className="mb-0 store-sales-table">
            <thead className="store-sales-table__head">
              <tr>
                <th role="button" onClick={() => handleOrdersSort("name")}>
                  Project {renderOrdersSortIcon("name")}
                </th>
                <th
                  role="button"
                  onClick={() => handleOrdersSort("buyer_name")}
                >
                  Buyer {renderOrdersSortIcon("buyer_name")}
                </th>
                <th
                  role="button"
                  onClick={() => handleOrdersSort("buyer_total")}
                >
                  <div>Value {renderOrdersSortIcon("buyer_total")}</div>
                  <div className="small">
                    {fmtOrderRupee(ordersValueSum)}
                  </div>
                </th>
                <th>Status</th>
                <th role="button" onClick={() => handleOrdersSort("placed_at")}>
                  Placed {renderOrdersSortIcon("placed_at")}
                </th>
                {showAcceptedDateColumn ? (
                  <th
                    role="button"
                    onClick={() => handleOrdersSort("accepted_at")}
                  >
                    Accepted {renderOrdersSortIcon("accepted_at")}
                  </th>
                ) : null}
                {showRejectedDateColumn ? (
                  <th
                    role="button"
                    onClick={() => handleOrdersSort("rejected_at")}
                  >
                    Rejected {renderOrdersSortIcon("rejected_at")}
                  </th>
                ) : null}
                {showRejectedRemarksColumn ? (
                  <th style={{ minWidth: "12rem" }}>Rejection remarks</th>
                ) : null}
                <th>Payments</th>
                <th style={{ minWidth: "14rem" }}>Delivery Info</th>
                <th
                  role="button"
                  style={{ minWidth: "18rem" }}
                  onClick={() => handleOrdersSort("delivery_at")}
                >
                  Delivery {renderOrdersSortIcon("delivery_at")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.length === 0 ? (
                <tr>
                  <td
                    colSpan={ordersTableColSpan}
                    className="text-muted text-center py-4"
                  >
                    No store orders yet.
                  </td>
                </tr>
              ) : (
                sortedOrders.map((row) => {
                  const shipments = ensureArray(row?.shipments);
                  const statusDetailRaw = row?.status_detail_at ?? null;
                  const hasStatusDetail =
                    typeof statusDetailRaw === "string" &&
                    statusDetailRaw.trim().length > 0;
                  const statusDetailLine = hasStatusDetail
                    ? fmtRelativeTime(statusDetailRaw)
                    : "—";
                  const pname = String(row?.name || "").trim();
                  const pid = row?.id ?? "";
                  const orderLabel = formatStoreOrderLabel(row);
                  const orderBuyerId = row?.buyer_id;
                  const buyerLabel =
                    String(row?.buyer_name || "").trim() || "-";
                  const dealerName = String(row?.dealer_name || "").trim();
                  const hasDealerName = dealerName.length > 0;
                  const hasDealerPriceMeta =
                    row?.dealer_price_meta != null &&
                    typeof row.dealer_price_meta === "object";
                  const st = String(row?.status || "").trim();
                  const statusWord = formatStoreProjectStatusWord(st);
                  const isPlacedStatus = st === "placed";
                  const modeNorm = String(row?.mode ?? row?.order_mode ?? "")
                    .trim()
                    .toLowerCase();
                  const isDirect = modeNorm === "direct";
                  const totalPayment =
                    Number(row?.total_payment_received || 0) || 0;
                  const payableTotal = Number(row?.payable_total || 0) || 0;
                  const payableRounded =
                    payableTotal > 0 ? Math.round(payableTotal) : 0;
                  const advanceGate =
                    payableRounded > 0 ? Math.round(payableRounded * 0.1) : 0;
                  const totalPaymentRounded = Math.round(totalPayment);
                  const canAcceptDirect =
                    isDirect &&
                    isPlacedStatus &&
                    payableRounded > 0 &&
                    totalPaymentRounded >= advanceGate;
                  const isDealerAcceptedStatus = st === "dealer_accepted";
                  const canAcceptDealerOrder =
                    !isDirect &&
                    isDealerAcceptedStatus &&
                    payableRounded > 0 &&
                    totalPaymentRounded >= advanceGate;
                  const showAcceptButton = isDirect && isPlacedStatus;
                  const showDealerAdminAccept =
                    !isDirect && isDealerAcceptedStatus;
                  const isSampleOrder = row?.sample === true;
                  const isRejectedStatus = st === "rejected";
                  const showRejectButton = isPlacedStatus;
                  const canEditPayments = canAddPaymentsInSalesModal(
                    modeNorm,
                    st
                  );
                  const outstanding = Math.max(0, payableTotal - totalPayment);
                  const acceptDisabledTooltip = [
                    `Payable: ${fmtOrderRupee(payableTotal)}`,
                    `Advance (10%): ${fmtOrderRupee(advanceGate)}`,
                    `Received: ${fmtOrderRupee(totalPayment)}`,
                    outstanding > 0
                      ? `Outstanding: ${fmtOrderRupee(outstanding)}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  const isFullyPaid =
                    payableRounded > 0 && totalPaymentRounded >= payableRounded;
                  const acceptedAt = row?.accepted_at;
                  const rejectedAt = row?.rejected_at;
                  const rejectionRemarks = row?.rejection_remarks;
                  const placedAt = row?.placed_at;
                  const breakdownTitlePrefix = orderLabel || `Order #${pid}`;

                  return (
                    <tr key={row?.id ?? `${pname}-${pid}`}>
                      <td>
                        <Link
                          to={`/projects/${pid}`}
                          className="align-baseline text-decoration-none small"
                        >
                          <div className="d-flex flex-wrap align-items-baseline gap-2">
                            {orderLabel}
                          </div>
                          {isSampleOrder ? (
                            <span className="badge border border-secondary text-secondary mt-1">
                              sample
                            </span>
                          ) : null}
                        </Link>
                      </td>
                      <td>
                        {orderBuyerId != null ? (
                          <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-none text-body text-start"
                            onClick={() =>
                              openBuyersTabWithBuyerHighlight(orderBuyerId)
                            }
                          >
                            {buyerLabel}
                          </button>
                        ) : (
                          buyerLabel
                        )}
                        <div className="small text-muted mt-1">
                          {formatBuyerFirstUserLine({
                            name: row?.buyer_user_name,
                            mobile: row?.buyer_user_mobile,
                          })}
                        </div>
                        {hasDealerName ? (
                          <div className="small text-muted mt-1">
                            ({dealerName})
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <PriceMetaTotalCell
                          meta={row?.buyer_price_meta}
                          label={`${breakdownTitlePrefix} - Buyer breakdown`}
                          onOpen={openPriceMetaModal}
                        />
                        {hasDealerPriceMeta ? (
                          <div className="small text-muted mt-1">
                            (
                            <PriceMetaTotalCell
                              meta={row.dealer_price_meta}
                              label={`${breakdownTitlePrefix} - Dealer breakdown`}
                              onOpen={openPriceMetaModal}
                            />
                            )
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span>{statusWord}</span>
                          {!isDirect ? (
                            <span className="badge text-bg-dark">dealer</span>
                          ) : null}
                        </div>
                        <div
                          className="small text-muted mt-1"
                          title={
                            hasStatusDetail
                              ? fmtHoverDateTime(statusDetailRaw)
                              : undefined
                          }
                        >
                          {statusDetailLine}
                        </div>
                        {isPlacedStatus && !isDirect ? (
                          <div className="small text-muted mt-1">
                            Awaiting dealer approval
                          </div>
                        ) : null}
                        {isDealerAcceptedStatus && !isDirect ? (
                          <div className="small text-muted mt-1">
                            Awaiting payment
                          </div>
                        ) : null}
                        {isPlacedStatus && isDirect ? (
                          <div className="small text-muted mt-1">
                            Awaiting approval
                          </div>
                        ) : null}
                        {isRejectedStatus && isSampleOrder ? (
                          <div className="small text-muted mt-1">
                            {SAMPLE_ORDER_REJECTED_MESSAGE}
                          </div>
                        ) : null}
                        {showRejectButton ||
                        showAcceptButton ||
                        showDealerAdminAccept ? (
                          <div className="mt-2 d-flex flex-wrap gap-1 align-items-center">
                            {showRejectButton ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger py-0 px-2"
                                title="Reject this order"
                                onClick={() =>
                                  openOrderStatusConfirmModal(
                                    pid,
                                    "reject",
                                    orderLabel
                                  )
                                }
                              >
                                Reject
                              </button>
                            ) : null}
                            {showAcceptButton ? (
                              <span
                                className="d-inline-block"
                                title={
                                  !canAcceptDirect
                                    ? acceptDisabledTooltip
                                    : "Accept this direct order"
                                }
                              >
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success py-0 px-2"
                                  disabled={!canAcceptDirect}
                                  onClick={() => {
                                    if (canAcceptDirect) {
                                      openOrderStatusConfirmModal(
                                        pid,
                                        "accept",
                                        orderLabel
                                      );
                                    }
                                  }}
                                >
                                  Accept
                                </button>
                              </span>
                            ) : null}
                            {showDealerAdminAccept ? (
                              <span
                                className="d-inline-block"
                                title={
                                  !canAcceptDealerOrder
                                    ? acceptDisabledTooltip
                                    : "Accept this dealer order"
                                }
                              >
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success py-0 px-2"
                                  disabled={!canAcceptDealerOrder}
                                  onClick={() => {
                                    if (canAcceptDealerOrder) {
                                      openOrderStatusConfirmModal(
                                        pid,
                                        "accept",
                                        orderLabel
                                      );
                                    }
                                  }}
                                >
                                  Accept
                                </button>
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                      <td className="small">
                        {placedAt ? (
                          fmtHoverDateTime(placedAt)
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      {showAcceptedDateColumn ? (
                        <td className="small">
                          {acceptedAt ? (
                            fmtHoverDateTime(acceptedAt)
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      ) : null}
                      {showRejectedDateColumn ? (
                        <td className="small">
                          {rejectedAt ? (
                            fmtHoverDateTime(rejectedAt)
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      ) : null}
                      {showRejectedRemarksColumn ? (
                        <td className="small">
                          {rejectionRemarks ? (
                            rejectionRemarks
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      ) : null}
                      <td className="small" style={{ minWidth: "10rem" }}>
                        <div className="d-flex align-items-center gap-2 flex-wrap w-100">
                          {isFullyPaid ? (
                            <span
                              className="text-success fw-bold flex-shrink-0"
                              style={{ fontSize: "1.1rem", lineHeight: 1 }}
                              title="Full payment received"
                              aria-label="Full payment received"
                            >
                              ✓
                            </span>
                          ) : null}
                          <span className="fw-semibold">
                            {totalPayment === 0 ? (
                              <span className="text-muted">—</span>
                            ) : (
                              fmtOrderRupee(totalPayment)
                            )}
                          </span>
                          {canEditPayments ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary py-0 px-2 text-nowrap ms-auto"
                              onClick={() => openPaymentsModal(row)}
                              aria-label="Add or edit payments"
                              title="Add or edit payments"
                            >
                              Edit
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td className="small align-top">
                        <OrderDeliveryInfoCell row={row} />
                      </td>
                      <td>
                        {shipments.length === 0 ? (
                          <span className="text-muted">-</span>
                        ) : (
                          <div className="d-flex flex-column gap-2">
                            {shipments.map((shipment, shipmentIndex) =>
                              (() => {
                                const shipmentStatus = shipment.status;
                                const shipmentDate =
                                  shipmentStatus === "draft"
                                    ? shipment?.delivery_date
                                    : shipmentStatus === "dispatched"
                                    ? shipment?.dispatched_at
                                    : shipmentStatus === "delivered"
                                    ? shipment?.delivered_at
                                    : null;

                                const statusWord =
                                  formatStoreProjectStatusWord(shipmentStatus);
                                const dateLabel = shipmentDate
                                  ? fmtHoverDate(shipmentDate)
                                  : null;
                                const statusWithDate = dateLabel
                                  ? `${statusWord} (${dateLabel})`
                                  : statusWord;

                                return (
                                  <div
                                    key={
                                      shipment?.id ??
                                      `${
                                        row?.id ?? "order"
                                      }-shipment-${shipmentIndex}`
                                    }
                                    className="d-grid align-items-center gap-2 border rounded-2 px-2 py-1 bg-light"
                                    style={{
                                      gridTemplateColumns:
                                        "minmax(0,1fr) minmax(0,1fr) auto minmax(0,1.2fr) auto",
                                    }}
                                  >
                                    <span className="small fw-semibold text-truncate">
                                      {shipment?.name ||
                                        `Shipment ${shipmentIndex + 1}`}
                                    </span>
                                    <span className="small">
                                      {`${fmtOrderRupee(shipment.price)} (${
                                        shipment?.items_count
                                      })`}
                                    </span>
                                    <button
                                      type="button"
                                      className="btn fs-6 btn-link p-0"
                                      title="View invoice"
                                      aria-label="View invoice"
                                      onClick={() =>
                                        openShipmentInvoiceModal(shipment)
                                      }
                                    >
                                      <i
                                        className="fa-solid fa-circle-info"
                                        aria-hidden="true"
                                      />
                                    </button>
                                    <span className="small">
                                      {statusWithDate}
                                    </span>
                                    <div className="d-flex flex-wrap gap-1 justify-content-end">
                                      {shipmentStatus === "draft" ? (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary py-0 px-2"
                                          disabled={!shipment.dispatchable}
                                          onClick={() =>
                                            openDispatchShipmentModal(
                                              shipment,
                                              shipmentIndex
                                            )
                                          }
                                        >
                                          Mark as Dispatch
                                        </button>
                                      ) : null}
                                      {shipmentStatus == "dispatched" ? (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary py-0 px-2"
                                          onClick={() =>
                                            openDeliverShipmentModal(
                                              shipment,
                                              shipmentIndex
                                            )
                                          }
                                        >
                                          Mark as delivered
                                        </button>
                                      ) : null}
                                    </div>
                                  </div>
                                );
                              })()
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </>
      )}

      <OrderPriceMetaBreakdownModal
        isOpen={priceMetaModal.isOpen}
        toggle={closePriceMetaModal}
        title={priceMetaModal.title}
        meta={priceMetaModal.meta}
      />

      <Modal
        isOpen={orderStatusConfirmModal.isOpen}
        toggle={() =>
          !orderStatusConfirmModal.loading && closeOrderStatusConfirmModal()
        }
        centered
      >
        <ModalHeader
          toggle={() =>
            !orderStatusConfirmModal.loading && closeOrderStatusConfirmModal()
          }
        >
          {orderStatusConfirmModal.action === "accept"
            ? "Confirm accept"
            : "Confirm reject"}
        </ModalHeader>
        <ModalBody>
          {orderStatusConfirmModal.action === "accept" ? (
            <>
              Accept{" "}
              <span className="fw-semibold">
                {orderStatusConfirmModal.orderLabel}
              </span>
              ? The order will move to <strong>Accepted</strong> and production
              workflows can proceed.
            </>
          ) : (
            <>
              Reject{" "}
              <span className="fw-semibold">
                {orderStatusConfirmModal.orderLabel}
              </span>
              ? This cannot be undone — the order cannot be moved back to quote.
              <FormGroup className="mt-3 mb-0">
                <Label for="orderRejectRemarks" className="small fw-semibold">
                  Rejection remarks <span className="text-danger">*</span>
                </Label>
                <Input
                  id="orderRejectRemarks"
                  type="textarea"
                  rows={3}
                  value={orderStatusConfirmModal.rejectionRemarks}
                  onChange={(e) =>
                    setOrderStatusConfirmModal((prev) => ({
                      ...prev,
                      rejectionRemarks: e.target.value,
                    }))
                  }
                  placeholder="Reason for rejecting this order"
                  disabled={orderStatusConfirmModal.loading}
                />
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={closeOrderStatusConfirmModal}
            disabled={orderStatusConfirmModal.loading}
          >
            Cancel
          </Button>
          <Button
            color={
              orderStatusConfirmModal.action === "accept" ? "success" : "danger"
            }
            onClick={handleConfirmOrderStatusChange}
            disabled={
              orderStatusConfirmModal.loading ||
              (orderStatusConfirmModal.action === "reject" &&
                !String(orderStatusConfirmModal.rejectionRemarks || "").trim())
            }
          >
            {orderStatusConfirmModal.loading
              ? "Updating…"
              : orderStatusConfirmModal.action === "accept"
              ? "Accept"
              : "Reject"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default StoreSalesDashboardOrdersSection;
